/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, type OnInit, ChangeDetectorRef, Component, EventEmitter, Injector, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { type ColumnDef, type Params, type TableConfig, I18nService, TableAbstract } from '@dm/core';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InfigoService } from '../../../../infigo.service';

interface QueryLUNParams {
    lunGroupId?: string;
    hostId?: string;
    hostGroupId: string;
}

@Component({
    selector: 'app-mapping-member-lun',
    templateUrl: './mapping-member-lun.component.html',
    styleUrls: ['./mapping-member-lun.component.scss', '../mapping-table.component.scss'],
})
export class MappingMemberLunComponent extends TableAbstract<any> implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: QueryLUNParams;
    @Input() filterParams;
    @Output() searchChange = new EventEmitter<any>();
    @Output() setRouter = new EventEmitter<any>();
    lunsList;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    $destroy = new Subject<void>();
    loading = true;
    @ViewChild('nameTemp') nameTemp: TemplateRef<any>;

    constructor(
        public i18n: I18nService,
        public infigoService: InfigoService,
        public changeDetectorRef: ChangeDetectorRef,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.filterParams === undefined) {
            this.filterParams = {
                field: null,
            };
        }
        this.initTable();
    }

    ngAfterViewInit(): void {
        this.initTable();
    }

    initBtns(): void {
    }

    ngOnDestroy(): void {
        this.$destroy.next();
    }

    isHostGroup(): boolean {
        return this.data && this.data.hostGroupId === '';
    }

    initTable(): void {
        // 类型为主机组时下发主机组ID，类型为主机时下发主机ID与LUN组ID
        const requestParams = this.isHostGroup() ? {
            lunGroupId: this.data.lunGroupId,
            hostId: this.data.hostId,
        } : {
            hostGroupId: this.data.hostGroupId,
            lunGroupId: this.data.lunGroupId,
        };
        this.getLunGroupData(requestParams);
        this.tableConfig = {
            selectionMode: null,
            dataKey: 'lunId',
            filterLabel: false,
            motion: false,
            rows: 5,
            beforeQuery: (params: Params): Params => {
                this.searchChange.emit(cloneDeep({ ...params.filters[0], lunGroupId: this.data.lunGroupId }));
                return params;
            },
        };

        this.tableColumnDefs = [
            {
                field: '',
                header: '',
                width: 100,
            },
            {
                field: 'lunName',
                header: this.i18n.get('common.LUNName_column'),
                filterType: 'search',
                filterCondition: this.getFilterCondition('lunName'),
                customTemplate: this.nameTemp,
            },
            {
                field: 'lunId',
                header: this.i18n.get('common.ID_column'),
                filterType: 'search',
                hidden: true,
                filterCondition: this.getFilterCondition('lunId'),
            },
            {
                field: 'hostLunId',
                header: this.i18n.get('host.host_task_mappingLun_hostLunID_lab'),
                filterType: 'search',
                filterCondition: this.getFilterCondition('hostLunId'),
            },
        ];
    }

    /**
     * 获取LUNGroup所属Lun
     * @param params
     */
    getLunGroupData(params): void {
        this.infigoService.getHostLun(params).pipe(takeUntil(this.$destroy))
            .subscribe((res) => {
                this.lunsList = res.data;
                this.loading = false;
                this.changeDetectorRef.detectChanges();
            });
    }

    /**
     * 获取过滤条件
     * @param field 字段
     */
    getFilterCondition(field: string): any {
        return this.filterParams.field === field && this.filterParams.lunGroupId === this.data.lunGroupId ?
            this.filterParams.value :
            undefined;
    }

    setRouterPath(value): void {
        this.setRouter.emit(value);
    }
}
